/**
 * Tags API services
 */

import apiClient from './client';
import type { Tag } from '@/types';

export interface TagCreate {
  name: string;
  color?: string;
}

export interface TagUpdate {
  name?: string;
  color?: string;
}

const transformTag = (data: any): Tag => ({
  id: data.id,
  tenantId: data.tenant_id,
  name: data.name,
  color: data.color,
  createdAt: data.created_at,
  updatedAt: data.updated_at,
});

export const tagsApi = {
  list: async (params?: { search?: string }): Promise<Tag[]> => {
    const response = await apiClient.get('/v1/tags', { params });
    // Handle both array and {items: []} response formats
    const items = Array.isArray(response.data) ? response.data : (response.data.items || []);
    return items.map(transformTag);
  },

  get: async (id: string): Promise<Tag> => {
    const response = await apiClient.get(`/v1/tags/${id}`);
    return transformTag(response.data);
  },

  create: async (data: TagCreate): Promise<Tag> => {
    const response = await apiClient.post('/v1/tags', {
      name: data.name,
      color: data.color || '#52c41a',
    });
    return transformTag(response.data);
  },

  update: async (id: string, data: TagUpdate): Promise<Tag> => {
    const payload: any = {};
    if (data.name !== undefined) payload.name = data.name;
    if (data.color !== undefined) payload.color = data.color;

    const response = await apiClient.patch(`/v1/tags/${id}`, payload);
    return transformTag(response.data);
  },

  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/tags/${id}`);
  },

  createBulk: async (tags: TagCreate[]): Promise<Tag[]> => {
    const response = await apiClient.post('/v1/tags/bulk', tags.map(t => ({
      name: t.name,
      color: t.color || '#52c41a',
    })));
    return response.data.map(transformTag);
  },
};
